/**
 * The arrival time on the date step: Flexible Time first, then every fixed
 * time at once, grouped into morning and afternoon.
 *
 * It replaces `Timer` on this step only (the tasks flow keeps `Timer`). The
 * old grid showed half the day behind an am/pm arrow, so a customer looking
 * for the cheaper afternoon time had to know to look for it; and it marked
 * cheaper times with a "Best" badge that ended up on every morning time at
 * once. Here a cheaper time says how much cheaper, on the button, and
 * nothing else is marked.
 *
 * A saving is printed as "$14.80 off" — the calendar's word, the cart's number.
 *
 * The word matches the tiles one column over, which say "$14 off": the same
 * discount should not wear two different faces on one screen. The amount does
 * not match them, and deliberately. A tile rounds down because it is promising
 * something about a day the customer has not opened yet, and the cart beating
 * that promise reads as a bonus. A slot is the last thing touched before the
 * cart charges, and the two sit side by side — a slot saying "$14 off" beside a
 * cart line saying "−$14.80" reads as a discrepancy, not as a bonus. So the
 * slot prints what will actually be charged, to the cent.
 *
 * Whole dollars drop their cents: "$10 off", not "$10.00 off". Same number,
 * less to read, and the Flexible Time credit is a whole dollar amount nearly
 * always.
 */

import { formatCents } from "@homero/pricing";
import styled, { keyframes } from "styled-components";
import { disabledTime, formatTime } from "./Timer";
import { SAVINGS } from "./savings";

const BLUE = "var(--blue-300)";

/**
 * A cent amount as the buttons print it: "$16.80", and "$10" when the cents are
 * zero — `formatCents` always carries two decimals, and a whole dollar has
 * nothing to say with them. Stripped from the end so a thousands separator
 * survives.
 */
function money(cents: number): string {
  return formatCents(cents).replace(/\.00$/, "");
}

/**
 * A saving as the buttons print it, to the cent, or nothing when there is none.
 *
 * No floor here, unlike the calendar's `badgeSavings`. That floor exists because
 * a tile rounds down and 80c would print as "$0 off"; printed exactly, 80c is
 * "$0.80 off", which is small but true — and it is the number the cart will
 * take off, so hiding it would leave the cart with an unexplained line.
 */
function badge(cents: number | null): string | null {
  if (cents === null || cents === 0) return null;
  /**
   * Both directions. A time-of-day rule can make a slot dearer as well as
   * cheaper, and a button that printed the saving but stayed blank for the
   * surcharge would quietly hide the one number a customer would want before
   * picking it -- while the cart charged it anyway.
   */
  return cents > 0 ? `${money(cents)} off` : `${money(-cents)} more`;
}

/** The times the funnel has always offered, the same list `Timer` shows. */
const GROUPS = [
  { label: "Morning", times: formatTime("am") },
  { label: "Afternoon", times: formatTime("pm") },
];

const Picker = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

/**
 * The saving, when it has just been earned: a short scale up and back, with a
 * ring blooming out of the badge behind it.
 *
 * On the button that was clicked and nowhere else, so the reward is tied to the
 * click that caused it rather than announced from across the card. It runs once
 * and leaves nothing behind — a badge that stayed bigger, or kept pulsing,
 * would be a permanent decoration rather than an answer to an action.
 */
const pop = keyframes`
  0%   { transform: scale(1); }
  45%  { transform: scale(1.09); }
  100% { transform: scale(1); }
`;

const ring = keyframes`
  0%   { transform: scale(0.7); opacity: 0.45; }
  100% { transform: scale(1.7); opacity: 0; }
`;

const Choice = styled.button<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  font-family: inherit;
  color: ${(p) => (p.$selected ? "#fff" : "#101322")};
  background: ${(p) => (p.$selected ? BLUE : "#fff")};
  border: 1px solid ${(p) => (p.$selected ? BLUE : "#e2e2e2")};
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 140ms ease, border-color 140ms ease;

  &:hover:not([aria-disabled="true"]) {
    border-color: ${(p) => (p.$selected ? BLUE : "#c6ccff")};
    background: ${(p) => (p.$selected ? BLUE : "#f8f9ff")};
  }

  &:focus-visible {
    outline: 2px solid ${BLUE};
    outline-offset: 2px;
  }

  &[aria-disabled="true"] {
    cursor: not-allowed;
    opacity: 0.45;
  }

  strong {
    display: block;
    font-size: 14px;
    font-weight: 600;
  }

  small {
    display: block;
    font-size: 12px;
    color: ${(p) => (p.$selected ? "rgba(255, 255, 255, 0.8)" : "#6b7187")};
  }

  .prices {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
  }

  .save {
    position: relative;
    font-size: 12px;
    color: ${(p) => (p.$selected ? SAVINGS.onBlue : SAVINGS.ink)};
  }

  .save.flex {
    font-size: 13px;
  }

  /*
    The breakdown under the headline. Grey rather than a third green thing on
    one button: green on this step means "costs less", and the number that says
    how much less is the one above it.
  */
  .parts {
    font-size: 11px;
    line-height: 13px;
    font-weight: 500;
    color: ${(p) => (p.$selected ? "rgba(255, 255, 255, 0.78)" : "#6b7187")};
    /*
      The one line on this button allowed to wrap, overriding the nowrap above.
      At 320px it is 6px wider than the space left beside the arrival sentence
      and was clipped at the button's edge; everywhere above ~360px it stays on
      one line by itself. A breakpoint would have to guess where that is.
    */
    white-space: normal;
    text-align: right;
  }

  /*
    Each half is one unbreakable unit, so a narrow column breaks the line at the
    plus rather than between an amount and the word that says what it is for.
    Unstyled, the desktop column broke after "$16.80" and left "nearby" alone on
    the second line.
  */
  .parts span {
    white-space: nowrap;
  }

  .save.pop {
    animation: ${pop} 220ms ease-out;
  }

  .save.pop::after {
    content: "";
    position: absolute;
    inset: -4px -6px;
    border-radius: 999px;
    border: 1px solid ${(p) => (p.$selected ? SAVINGS.onBlue : SAVINGS.mark)};
    animation: ${ring} 420ms ease-out forwards;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    .save.pop {
      animation: none;
    }

    .save.pop::after {
      display: none;
    }
  }
`;

/**
 * The line between Flexible Time and the clock times.
 *
 * The grid underneath is already the answer, but only to someone who reads a
 * row of clock times as an offer rather than as the leftovers of the option
 * above it. This says the offer out loud, once, in the gap between the two.
 *
 * Not a boxed callout: it sits in the flow like the "Morning" label below it,
 * because it is a heading for what follows, not a warning about it.
 */
const SpecificTime = styled.div`
  margin-top: 4px;

  strong {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #101322;
  }

  span {
    display: block;
    margin-top: 1px;
    font-size: 12px;
    color: #6b7187;
  }
`;

const GroupLabel = styled.p`
  margin: 6px 0 0;
  font-size: 12px;
  font-weight: 500;
  color: #575e75;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 400px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;

const Slot = styled(Choice)`
  flex-direction: column;
  justify-content: center;
  gap: 1px;
  height: 44px;
  padding: 0 4px;
  text-align: center;
  font-size: 13px;
  font-weight: ${(p) => (p.$selected ? 600 : 500)};
  font-variant-numeric: tabular-nums;

  .save,
  .note {
    font-size: 10.5px;
    line-height: 12px;
    font-weight: 600;
  }

  .note {
    color: #6b7187;
  }
`;

const Placeholder = styled.span`
  display: block;
  height: 44px;
  border-radius: 6px;
  background: #f3f4f8;
`;

/** One fixed time: booked, cheaper by some amount, or plain. */
function TimeSlot({
  time,
  selected,
  booked,
  disabled,
  savingCents,
  onTime,
}: {
  time: string;
  selected: boolean;
  booked: boolean;
  disabled: boolean;
  savingCents: number | null;
  onTime: (time: string) => void;
}) {
  const saving = badge(savingCents);
  return (
    <Slot
      type="button"
      data-time={time}
      $selected={selected}
      aria-pressed={selected}
      aria-disabled={disabled || booked || undefined}
      aria-label={
        booked
          ? `${time}, already booked`
          : saving
          ? `${time}, ${
              (savingCents as number) > 0
                ? `saves ${formatCents(savingCents as number)}`
                : `costs ${formatCents(-(savingCents as number))} more`
            }`
          : time
      }
      title={booked ? "Time is already booked" : undefined}
      onClick={() => !disabled && !booked && onTime(time)}
    >
      <span>{time}</span>
      {booked ? (
        <span className="note">Booked</span>
      ) : saving ? (
        <span className={selected ? "save pop" : "save"}>{saving}</span>
      ) : null}
    </Slot>
  );
}

export function ArrivalPicker({
  cleanTime,
  isFlexible,
  disabled,
  loading,
  bookedTimes,
  flexibleCents,
  savingCents,
  onTime,
  onFlexible,
}: {
  cleanTime: string;
  isFlexible: boolean;
  /** No date chosen yet. */
  disabled: boolean;
  loading: boolean;
  bookedTimes: string[];
  /** The Flexible Time discount on the chosen day, from the config. */
  flexibleCents: number;
  /** What choosing this arrival saves on the chosen day, or null. */
  savingCents: (arrival: string) => number | null;
  onTime: (time: string) => void;
  onFlexible: () => void;
}) {
  const flexibleSaving = savingCents("flexible");
  const chosen = isFlexible && !disabled;

  /**
   * One number, because the customer is making one comparison.
   *
   * The row used to print the two savings stacked — "$10 off" over "$16.80 off
   * nearby" — and left it to the reader to work out whether they add, replace
   * one another, or are the same money counted twice. They add: the arrival
   * credit for not naming a time, and the nearby discount the day is offering.
   *
   * Worse, the nearby figure alone was no argument for Flexible Time. A fixed
   * arrival inside the day's nearby window earns exactly the same $16.80, so the
   * row was advertising a saving you do not have to choose it to get. The sum is
   * the honest headline: it is what this choice is worth against the slots
   * below, which print their own saving the same way — $26.80 here against
   * $16.80 there, and the $10 gap is the decision.
   *
   * The parts stay, one size down, for the reader who wants to know where the
   * number came from. Only when there are two of them: "$10 flexible" alone
   * under "$10 off" is the same fact twice.
   */
  const nearby = flexibleSaving ?? 0;
  const worth = badge(flexibleCents + nearby);
  const itemised = flexibleCents > 0 && nearby > 0;

  return (
    <Picker>
      <Choice
        type="button"
        data-time="flexible"
        $selected={chosen}
        aria-pressed={chosen}
        aria-disabled={disabled || undefined}
        // Choosing it again keeps it chosen, like a time; it used to toggle off.
        onClick={() => !disabled && !isFlexible && onFlexible()}
      >
        <span>
          <strong>I&rsquo;m flexible</strong>
          <small>Cleaner will arrive between 9:00am and 4:00pm</small>
        </span>
        <span className="prices">
          {/* Green like every other saving on this step: green means it costs less. */}
          {worth ? (
            <span
              className={chosen ? "save flex pop" : "save flex"}
              aria-label={
                itemised
                  ? `saves ${formatCents(flexibleCents + nearby)}: ` +
                    `${formatCents(flexibleCents)} for being flexible, and ` +
                    `${formatCents(nearby)} because we are already cleaning nearby`
                  : `saves ${formatCents(flexibleCents + nearby)}`
              }
            >
              {worth}
            </span>
          ) : null}
          {/* The headline's own aria-label reads the parts, so this is decoration. */}
          {itemised ? (
            <span className="parts" aria-hidden="true">
              <span>{money(flexibleCents)} flexible</span>{" + "}
              <span>{money(nearby)} nearby</span>
            </span>
          ) : null}
        </span>
      </Choice>

      <SpecificTime>
        <strong>Need a specific time?</strong>
        <span>The cleaner will arrive at your requested time.</span>
      </SpecificTime>

      {GROUPS.map((group) => (
        <div key={group.label}>
          <GroupLabel>{group.label}</GroupLabel>
          <Grid style={{ marginTop: 8 }}>
            {group.times.map((time) => {
              if (loading) return <Placeholder key={time} aria-hidden="true" />;
              const booked = disabledTime(time, bookedTimes);
              return (
                <TimeSlot
                  key={time}
                  time={time}
                  selected={!isFlexible && cleanTime === time}
                  booked={booked}
                  disabled={disabled}
                  savingCents={booked ? null : savingCents(time)}
                  onTime={onTime}
                />
              );
            })}
          </Grid>
        </div>
      ))}
    </Picker>
  );
}
